import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  AutoAwesomeRounded,
  CalendarMonthRounded,
  FilterListRounded,
  StarRounded,
  UpcomingRounded,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import DatesItem from "./DatesItem";
import {
  EmptyState,
  PageIntro,
  PageShell,
  SectionBlock,
  StatStrip,
  SurfaceCard,
} from "../ui/AppPrimitives";
import { startOfDay } from "../utils/dateUtils";
import { getRelativeDayLabel } from "../utils/dateUtils";

const filterSections = [
  {
    title: "View",
    options: [
      { value: "all", label: "All Dates" },
      { value: "important", label: "Important Only" },
    ],
  },
  {
    title: "Time",
    options: [
      { value: "today", label: "Today" },
      { value: "week", label: "This Week" },
      { value: "month", label: "This Month" },
    ],
  },
  {
    title: "Category",
    options: [
      { value: "personal", label: "Personal" },
      { value: "legal", label: "Legal" },
      { value: "health", label: "Health" },
      { value: "exam", label: "Exam" },
      { value: "other", label: "Other" },
    ],
  },
];

const Dates = ({ dates, onDelete, onPriority }) => {
  const [filter, setFilter] = React.useState("all");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isOpen = Boolean(anchorEl);

  const grouped = React.useMemo(() => {
    const today = startOfDay(new Date());
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const endOfYear = new Date(today.getFullYear(), 11, 31);

    const result = {
      today: [],
      week: [],
      month: [],
      year: [],
      later: [],
    };

    const applyFilter = (item) => {
      if (!item.dateTime || item.type === "recurring") return false;
      const eventDate = startOfDay(item.dateTime);

      switch (filter) {
        case "important":
          return item.priority && eventDate >= today;
        case "today":
          return eventDate.getTime() === today.getTime();
        case "week":
          return eventDate > today && eventDate <= endOfWeek;
        case "month":
          return eventDate > today && eventDate <= endOfMonth;
        case "personal":
        case "legal":
        case "health":
        case "exam":
        case "other":
          return item.category?.toLowerCase() === filter && eventDate >= today;
        default:
          return eventDate >= today;
      }
    };

    dates.filter(applyFilter).forEach((item) => {
      const itemDate = startOfDay(item.dateTime);
      if (itemDate.getTime() === today.getTime()) {
        result.today.push(item);
      } else if (itemDate <= endOfWeek) {
        result.week.push(item);
      } else if (itemDate <= endOfMonth) {
        result.month.push(item);
      } else if (itemDate <= endOfYear) {
        result.year.push(item);
      } else {
        result.later.push(item);
      }
    });

    Object.keys(result).forEach((key) => {
      result[key].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
    });

    return result;
  }, [dates, filter]);

  const filterLabel =
    filterSections
      .flatMap((section) => section.options)
      .find((option) => option.value === filter)?.label || "All Dates";

  const upcomingSingles = dates.filter((item) => item.type !== "recurring" && startOfDay(item.dateTime) >= startOfDay(new Date()));
  const importantCount = upcomingSingles.filter((item) => item.priority).length;
  const todayCount = grouped.today.length;
  const weekCount = grouped.week.length;
  const monthCount = grouped.month.length;
  const nextEvent = upcomingSingles
    .slice()
    .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))[0];
  const focusAreas = React.useMemo(() => {
    const counts = upcomingSingles.reduce((acc, item) => {
      const key = item.category || "Other";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [upcomingSingles]);

  const renderGroup = (label, items) => {
    if (items.length === 0) return null;

    return (
      <SectionBlock key={label} title={label} subtitle={`${items.length} event${items.length > 1 ? "s" : ""}`}>
        <Stack spacing={1.6}>
          {items.map((item) => (
            <DatesItem
              key={item.id}
              dateItem={item}
              onDelete={onDelete}
              onPriority={onPriority}
            />
          ))}
        </Stack>
      </SectionBlock>
    );
  };

  return (
    <PageShell maxWidth="xl">
      <PageIntro
        eyebrow="Dashboard"
        title="A sharper command center for every important date."
        subtitle="See your timeline with stronger hierarchy, cleaner signal, and a polished overview of what needs attention next."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: { xs: "100%", md: "auto" } }}>
            
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<FilterListRounded />}
              onClick={(event) => setAnchorEl(event.currentTarget)}
            >
              {filterLabel}
            </Button>
          </Stack>
        }
      />

      <StatStrip
        items={[
          {
            label: "Upcoming",
            value: upcomingSingles.length,
            caption: "Future one-time events",
          },
          {
            label: "Priority",
            value: importantCount,
            caption: "Marked as important",
            tint: "#f59e0b",
          },
        ]}
      />

      {Object.values(grouped).every((arr) => arr.length === 0) ? (
        <EmptyState
          icon={<UpcomingRounded color="primary" sx={{ fontSize: 40 }} />}
          title="No upcoming dates yet"
          description="Start building your timeline with exams, appointments, legal milestones, or personal events."
          actionLabel="Add your first date"
          actionTo="/add"
        />
      ) : (
        <Box>
          {renderGroup("Today", grouped.today)}
          {renderGroup("This Week", grouped.week)}
          {renderGroup("This Month", grouped.month)}
          {renderGroup("This Year", grouped.year)}
          {renderGroup("Later", grouped.later)}
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 260,
            borderRadius: 4,
            mt: 1,
          },
        }}
      >
        {filterSections.map((section) => (
          <Box key={section.title} sx={{ py: 0.4 }}>
            <Typography
              variant="overline"
              sx={{ px: 2, color: "text.secondary", display: "block" }}
            >
              {section.title}
            </Typography>
            {section.options.map((option) => (
              <MenuItem
                key={option.value}
                selected={filter === option.value}
                onClick={() => {
                  setFilter(option.value);
                  setAnchorEl(null);
                }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ width: "100%" }}>
                  <span>{option.label}</span>
                  {filter === option.value && <Chip label="Active" size="small" color="primary" />}
                </Stack>
              </MenuItem>
            ))}
          </Box>
        ))}
      </Menu>
    </PageShell>
  );
};

export default Dates;
